<template>
	<div>
		<div class="stage">
			<div class="row mb-4 ml-1">
				<h1 class="font-medium">Realtime</h1>
			</div>
			<div v-show="showCharts" class="mb-2 flex w-full flex-wrap justify-between font-semibold">
				<div class="flex w-full flex-wrap gap-2">
					<div class="group flex w-[calc(50%-0.25rem)] lg:w-[calc(25%-0.4rem)]" @click="setDisplay('calls')">
						<div
							class="flex w-full flex-col justify-between space-y-1 rounded-lg border-b-2 border-green-400 bg-green-500 p-3 text-green-900 transition hover:bg-green-600 active:bg-green-700 xs:space-y-2 xs:border-b-4 xs:p-6"
						>
							<div
								class="border-b border-green-400 pb-2 text-xs font-semibold uppercase transition-colors group-hover:text-green-200 group-active:text-green-400 xs:text-sm"
							>
								Calls
							</div>
							<div class="pb-1 text-2xl font-bold text-white xs:text-5xl">
								{{ totalCalls || '0' }}
							</div>
							<div class="mt-2 flex flex-wrap gap-2">
								<span
									v-if="
										!selectedTenant &&
										tenants?.length > 1 &&
										!selectedAccount &&
										accounts?.length > 1
									"
									class="flex items-center gap-1 rounded bg-green-900/50 px-2 py-1 text-xs text-white"
								>
									<span class="fa-light fa-filter-slash"></span>
									All Calls
								</span>
								<span
									v-if="selectedTenant && tenants?.length > 1"
									class="flex items-center gap-1 rounded bg-green-900/50 px-2 py-1 text-xs text-white"
								>
									<span class="fa-light fa-filter"></span>
									Tenant
								</span>
								<span
									v-if="selectedAccount && accounts?.length > 1"
									class="flex items-center gap-1 rounded bg-green-900/50 px-2 py-1 text-xs text-white"
								>
									<span class="fa-light fa-filter"></span>
									Account
								</span>
							</div>
						</div>
					</div>
					<div
						class="group flex w-[calc(50%-0.25rem)] lg:w-[calc(25%-0.4rem)]"
						@click="setDisplay('registrations')"
					>
						<div
							class="flex w-full flex-col justify-between space-y-1 rounded-lg border-b-2 border-orange-400 bg-orange-500 p-3 text-orange-900 transition hover:bg-orange-600 active:bg-orange-700 xs:space-y-2 xs:border-b-4 xs:p-6"
						>
							<div
								class="hidden border-b border-orange-400 pb-2 text-xs font-semibold uppercase transition-colors group-hover:text-orange-200 group-active:text-orange-400 xs:block xs:text-sm"
							>
								SIP Trunk Registrations
							</div>
							<div
								class="block border-b border-orange-400 pb-2 text-xs font-semibold uppercase transition-colors group-hover:text-orange-200 group-active:text-orange-400 xs:hidden xs:text-sm"
							>
								Trunk Registrations
							</div>
							<div class="pb-1 text-2xl font-bold text-white xs:text-5xl">
								{{ registrationsByTenantAndAccount.length || '0' }}
							</div>
							<div class="mt-2 flex flex-wrap gap-2">
								<span
									v-if="
										!selectedTenant &&
										tenants?.length > 1 &&
										!selectedAccount &&
										accounts?.length > 1
									"
									class="flex items-center gap-1 rounded bg-green-900/50 px-2 py-1 text-xs text-white"
								>
									<span class="fa-light fa-filter-slash"></span>
									All Calls
								</span>
								<span
									v-if="selectedTenant && tenants?.length > 1"
									class="flex items-center gap-1 rounded bg-green-900/50 px-2 py-1 text-xs text-white"
								>
									<span class="fa-light fa-filter"></span>
									Tenant
								</span>
								<span
									v-if="selectedAccount && accounts?.length > 1"
									class="flex items-center gap-1 rounded bg-green-900/50 px-2 py-1 text-xs text-white"
								>
									<span class="fa-light fa-filter"></span>
									Account
								</span>
							</div>
						</div>
					</div>
					<div
						v-if="$global.hasUserRole(userRoles, 'tenants:getall')"
						class="group flex w-[calc(50%-0.25rem)] lg:w-[calc(25%-0.4rem)]"
						@click="setDisplay('mobilesims')"
					>
						<div
							class="flex w-full flex-col justify-between space-y-1 rounded-lg border-b-2 border-red-400 bg-red-500 p-3 text-red-900 transition hover:bg-red-600 active:bg-red-700 xs:space-y-2 xs:border-b-4 xs:p-6"
						>
							<div
								class="mt-0 border-b border-red-400 pb-2 text-xs font-semibold uppercase transition-colors group-hover:text-red-200 group-active:text-red-400 xs:text-sm"
							>
								<span class="hidden xs:block">Connected Mobile SIMs</span>
								<span class="block xs:hidden">Connected SIMs</span>
							</div>
							<div class="pb-1 text-2xl font-bold text-white xs:text-5xl">
								{{ registeredSIMsByTenantAndAccount.length || '0' }}
							</div>
							<div class="mt-2 flex flex-wrap gap-2">
								<span
									v-if="
										!selectedTenant &&
										tenants?.length > 1 &&
										!selectedAccount &&
										accounts?.length > 1
									"
									class="flex items-center gap-1 rounded bg-green-900/50 px-2 py-1 text-xs text-white"
								>
									<span class="fa-light fa-filter-slash"></span>
									All SIMs
								</span>
								<span
									v-if="selectedTenant && tenants?.length > 1"
									class="flex items-center gap-1 rounded bg-green-900/50 px-2 py-1 text-xs text-white"
								>
									<span class="fa-light fa-filter"></span>
									Tenant
								</span>
								<span
									v-if="selectedAccount && accounts?.length > 1"
									class="flex items-center gap-1 rounded bg-green-900/50 px-2 py-1 text-xs text-white"
								>
									<span class="fa-light fa-filter"></span>
									Account
								</span>
							</div>
						</div>
					</div>
					<div class="flex w-[calc(50%-0.25rem)] lg:w-[calc(25%-0.4rem)]">
						<div
							class="flex w-full cursor-default select-none flex-col justify-between space-y-1 rounded-lg border-b-2 border-blue-400 bg-blue-500 p-3 text-blue-900 transition xs:space-y-2 xs:border-b-4 xs:p-6"
						>
							<div
								class="hidden border-b border-blue-400 pb-2 text-xs font-semibold uppercase transition-colors xs:block xs:text-sm"
							>
								Total Account Cost
							</div>
							<div
								class="block border-b border-blue-400 pb-2 text-xs font-semibold uppercase transition-colors xs:hidden xs:text-sm"
							>
								Account Cost
							</div>
							<div class="pb-1 text-2xl font-bold text-white xs:text-5xl">
								£{{ totalAccountCost.toFixed(2) || '0.00' }}
							</div>
							<div class="mt-2 flex flex-wrap gap-2">
								<span
									v-if="
										!selectedTenant &&
										tenants?.length > 1 &&
										!selectedAccount &&
										accounts?.length > 1
									"
									class="flex items-center gap-1 rounded bg-green-900/50 px-2 py-1 text-xs text-white"
								>
									<span class="fa-light fa-filter-slash"></span>
									All Calls
								</span>
								<span
									v-if="selectedTenant && tenants?.length > 1"
									class="flex items-center gap-1 rounded bg-green-900/50 px-2 py-1 text-xs text-white"
								>
									<span class="fa-light fa-filter"></span>
									Tenant
								</span>
								<span
									v-if="selectedAccount && accounts?.length > 1"
									class="flex items-center gap-1 rounded bg-green-900/50 px-2 py-1 text-xs text-white"
								>
									<span class="fa-light fa-filter"></span>
									Account
								</span>
							</div>
						</div>
					</div>
				</div>
			</div>
			<div v-if="tenants?.length > 1 || accounts?.length > 1" class="mb-4 flex w-full gap-2 pt-2">
				<div v-if="tenants?.length > 1" class="w-[calc(50%-0.25rem)] lg:w-[calc(25%-0.5rem)]">
					<p class="filter-label">Tenant</p>
					<div class="formGroup withIcon">
						<select v-model="selectedTenant" @change="changeTenant">
							<option value="" selected>All Tenants</option>
							<option v-for="tenant in tenants" :key="tenant.id" :value="tenant.id">
								{{ tenant.companyName }}
							</option>
						</select>
						<span class="fa-light fa-chevron-down"></span>
					</div>
				</div>
				<div v-if="accounts?.length > 1" class="w-[calc(50%-0.25rem)] lg:w-[calc(25%-0.5rem)]">
					<p class="filter-label">Account</p>
					<div class="formGroup withIcon">
						<select v-model="selectedAccount">
							<option value="" selected>All Accounts</option>
							<option v-for="account in tenantAccounts" :key="account.id" :value="account.id">
								{{ account.name }}
							</option>
						</select>
						<span class="fa-light fa-chevron-down"></span>
					</div>
				</div>
			</div>

			<!-- <div v-if="selectedTenant" class="row py-5 xs:pb-5 pt-0">
				<div class="flex flex-wrap gap-4 w-full justify-center">
					<div
						v-if="selectedTenant"
						class="bg-blue-500 rounded-full w-auto px-4 flex justify-between items-center text-white"
						:class="{ 'bg-red-500': hover === 'tenant', 'hover:bg-blue-600': hover !== 'tenant' }"
					>
						<p class="text-sm py-1 px-2">
							<span class="uppercase font-bold mr-1">tenant:</span>{{ selectedTenantName }}
						</p>
						<span
							class="fa-light fa-close cursor-pointer ml-2"
							@mouseover="hover = 'tenant'"
							@mouseleave="hover = null"
							@click="clearSelectedTenant(), (hover = null)"
						></span>
					</div>
				</div>
			</div> -->

			<div class="flex items-center justify-center pb-4 md:pb-0">
				<div
					class="relative flex w-full flex-wrap gap-2 self-center rounded-lg p-0.5 md:w-[800px] md:flex-nowrap"
				>
					<button
						v-for="option in displayOptions"
						:key="option.value"
						type="button"
						class="relative w-[calc(50%-0.25rem)] cursor-default whitespace-nowrap rounded-full border border-main px-4 py-2 text-sm font-medium transition-all active:scale-[0.98] dark:border-main md:w-1/4"
						:class="{
							'border-transparent bg-main text-white dark:border-transparent dark:bg-accentMain dark:text-black':
								display === option.value,
							'bg-transparent text-black hover:bg-main/10 dark:text-light dark:hover:bg-offwhite/10':
								display !== option.value
						}"
						@click="setDisplay(option.value)"
					>
						<span class="hidden sm:block">{{ option.label }}</span>
						<span class="block sm:hidden">{{ option.shortLabel || option.label }}</span>
					</button>
				</div>
			</div>
			<div class="row margin-b20 middle-xs middle-sm middle-md middle-lg">
				<div class="col-xs-12 col-sm-4 col-md-4 col-lg-4">
					<p class="textSml textUppercase">
						{{ displayName }}
						({{ displayLength }})
					</p>
				</div>
				<div class="col-xs-12 col-sm-8 col-md-8 col-lg-8 text-right">
					<div class="flex items-center justify-end gap-8">
						<div v-if="display === 'calls'" class="flex items-center gap-2">
							<input
								id="combine-call-legs"
								v-model="combineCallLegs"
								type="checkbox"
								class="tw-toggle mr-0"
							/>
							<label for="combine-call-legs" class="cursor-pointer text-sm font-medium">
								Combine Call Legs
							</label>
						</div>
						<button
							class="inline-flex items-center gap-2 rounded-full bg-main px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-main/90 active:bg-main/80"
							@click="togglePolling"
						>
							<span class="fa-light" :class="{ 'fa-play': stopPolling, 'fa-pause': !stopPolling }"></span>
							<span>{{ stopPolling ? 'Resume' : 'Pause' }} Updates</span>
						</button>
					</div>
				</div>
			</div>
			<div class="row margin-b20">
				<cip-table
					v-if="display === 'calls'"
					:calls="callsByTenantAndAccount"
					@clear-call="clearCall"
				></cip-table>
				<registrations-table
					v-if="display === 'registrations'"
					:registrations="registrationsByTenantAndAccount"
				></registrations-table>
				<registered-sims-table
					v-if="display === 'mobilesims'"
					:registrations="registeredSIMsByTenantAndAccount"
				></registered-sims-table>
				<queued-calls-table
					v-if="display === 'queues'"
					:queued-calls="queuedCallsByTenantAndAccount"
					:call-legs="callsByTenantAndAccount"
				></queued-calls-table>
			</div>
		</div>
	</div>
</template>

<script>
import realtimeApi from '../scripts/realtime/realtime.api'
import accountsApi from '../scripts/accounts/accounts.api'
import flowsApi from '../scripts/flows/flows.api'

export default {
	inject: ['user', 'userRoles', 'tenants'],
	beforeRouteLeave() {
		this.stopPolling = true
	},
	data() {
		return {
			registrations: [],
			registeredSIMs: [],
			callLegs: [],
			accounts: [],
			queues: [],
			selectedTenant: '',
			selectedAccount: '',
			totalAccountCost: 0,
			totalTenantCost: 0,
			stopPolling: false,
			display: 'calls',
			showCharts: true,
			hover: null,
			pills: [],
			queuedCalls: [],
			flows: [],
			combineCallLegs: false,
			displayOptions: [
				{ value: 'calls', label: 'Calls in progress', shortLabel: 'Calls' },
				{ value: 'registrations', label: 'Registrations' },
				{ value: 'mobilesims', label: 'Mobile SIMs', shortLabel: 'SIMs' },
				{ value: 'queues', label: 'Queues' }
			]
		}
	},

	computed: {
		tenantAccounts() {
			return this.accounts
				.filter((account) => !this.selectedTenant || account.tenant?.id === parseInt(this.selectedTenant))
				.sort((a, b) => a.name.localeCompare(b.name))
		},
		totalCalls() {
			let totalCalls = [...new Set((this.callsByTenantAndAccount || []).map((cl) => cl.teleflowId))].length

			document.title = `${totalCalls} ${totalCalls === 1 ? 'call' : 'calls'} in progress | TeleFlow`

			return totalCalls
		},
		callsByTenantAndAccount() {
			const filteredCalls = this.callLegs
				.filter((call) => !this.selectedTenant || call.tenantId === parseInt(this.selectedTenant))
				.filter((call) => !this.selectedAccount || call.accountId === parseInt(this.selectedAccount))

			if (this.combineCallLegs) {
				const callsMap = new Map()
				filteredCalls.forEach((call) => {
					if (!callsMap.has(call.teleflowId) || call.leg === 'a') {
						callsMap.set(call.teleflowId, call)
					}
				})
				return Array.from(callsMap.values()).sort((a, b) => a.startTime - b.startTime)
			}

			return filteredCalls.sort((a, b) => {
				if (a.startTime !== b.startTime) {
					return a.startTime - b.startTime
				}
				if (a.teleflowId !== b.teleflowId) {
					return a.teleflowId.localeCompare(b.teleflowId)
				}
				return a.leg - b.leg
			})
		},
		queuedCallsByTenantAndAccount() {
			return this.queuedCalls
				.filter((call) => !this.selectedTenant || call.tenant?.id === parseInt(this.selectedTenant))
				.filter((call) => !this.selectedAccount || call.callLeg?.accountId === parseInt(this.selectedAccount))
				.sort((a, b) => a.queue_start_epoch - b.queue_start_epoch)
		},
		registrationsByTenantAndAccount() {
			return this.registrations
				.filter((reg) => !this.selectedTenant || reg.sipTrunk?.tenantId === parseInt(this.selectedTenant))
				.filter((reg) => !this.selectedAccount || reg.sipTrunk?.accountId === parseInt(this.selectedAccount))
		},
		registeredSIMsByTenantAndAccount() {
			return this.registeredSIMs
				.filter((reg) => !this.selectedTenant || reg.mobileSim?.tenantId === parseInt(this.selectedTenant))
				.filter((reg) => !this.selectedAccount || reg.mobileSim?.accountId === parseInt(this.selectedAccount))
		},
		selectedTenantName() {
			if (this.selectedTenant) {
				let tenant = this.tenants.find((t) => t.id === parseInt(this.selectedTenant))
				if (tenant) return tenant.companyName
			}
			return 'All Tenants'
		},
		selectedAccountName() {
			if (this.selectedAccount) {
				let account = this.accounts.find((a) => a.id === parseInt(this.selectedTenant))
				if (account) return account.name
			}
			return 'All Accounts'
		},
		displayName() {
			switch (this.display) {
				case 'calls':
					return this.combineCallLegs ? 'Calls' : 'Call legs'
				case 'registrations':
					return 'Registrations'
				case 'mobilesims':
					return 'Mobile SIMs'
				case 'queues':
					return 'Queues'
				default:
					return this.combineCallLegs ? 'Calls' : 'Call legs'
			}
		},
		displayLength() {
			switch (this.display) {
				case 'calls':
					return this.callsByTenantAndAccount.length
				case 'registrations':
					return this.registrationsByTenantAndAccount.length
				case 'mobilesims':
					return this.registeredSIMsByTenantAndAccount.length
				case 'queues':
					return this.queuedCallsByTenantAndAccount.length
				default:
					return this.callsByTenantAndAccount.length
			}
		}
	},

	watch: {
		selectedTenant: {
			handler(newValue, oldValue) {
				this.reloadData()
			}
		},
		'$route.query': {
			handler(newQuery) {
				const tab = newQuery.tab
				if (tab && ['calls', 'registrations', 'mobilesims', 'queues'].includes(tab)) {
					this.display = tab
				}
			},
			deep: true,
			immediate: true
		}
	},

	created() {
		if (this.$global.getSelectedTenant()) this.selectedTenant = this.$global.getSelectedTenant()

		accountsApi
			.getAll({ limit: 2500 })
			.then((response) => {
				this.accounts = response.data.results
			})
			.catch((error) => {
				console.log(error)
			})

		flowsApi
			.getAll()
			.then((response) => {
				this.flows = response.data.results
			})
			.catch((error) => {
				console.log(error)
			})
	},

	mounted() {
		if (this.tenants?.length === 1) this.selectedTenant = this.tenants[0].id

		const tab = this.$route.query.tab
		if (tab && ['calls', 'registrations', 'mobilesims', 'queues'].includes(tab)) {
			this.display = tab
		}

		this.reloadData()
	},

	beforeUnmount() {
		this.stopPolling = true
	},

	methods: {
		togglePolling() {
			this.stopPolling = !this.stopPolling
			if (!this.stopPolling) {
				this.reloadData()
			}
		},
		setDisplay(tab) {
			this.display = tab
			this.$router.push({ query: { ...this.$route.query, tab } })
		},
		changeTenant($event) {
			this.$global.setSelectedTenant($event)
			this.selectedAccount = ''
		},
		clearCall(clearCallRequest) {
			this.$swal({
				title: 'Kill Call',
				text: `Are you sure you want to kill this call?`,
				showCancelButton: true,
				confirmButtonText: 'Kill Call',
				cancelButtonText: 'Cancel',
				showDenyButton: false,
				denyButtonText: 'Cancel'
			}).then((result) => {
				if (result.isConfirmed)
					realtimeApi
						.clearCall(clearCallRequest)
						.then(() => {
							this.reloadData()
						})
						.catch((error) => {
							console.log(error)
						})
			})
		},
		reloadData() {
			realtimeApi
				.getAll({
					where: {
						selectedTenant: this.selectedTenant
					}
				})
				.then((response) => {
					const results = response.data

					const { callLegs, tenantBilling, accountBilling, registrations, sims, queues } = results

					this.registrations = registrations.map((reg) => {
						reg.tenant = this.tenants.find((t) => t.id === reg.sipTrunk?.tenantId)
						return reg
					})

					this.registeredSIMs =
						sims?.map((reg) => {
							reg.tenant = this.tenants.find((t) => t.id === reg.mobileSim?.tenantId)
							reg.account = this.accounts.find((a) => a.id === reg.mobileSim?.accountId)
							return reg
						}) || []

					this.callLegs = callLegs?.map((call) => {
						call.tenantBilling = tenantBilling.find(
							(tb) => tb.teleflowId === call.teleflowId && tb.leg === call.leg
						)
						call.accountBilling = accountBilling.find(
							(ab) => ab.teleflowId === call.teleflowId && ab.leg === call.leg
						)

						return call
					})

					this.queuedCalls =
						queues?.reduce((acc, curr) => {
							curr.calls?.forEach((call) => {
								acc.push({
									...call,
									flow: this.flows.find((f) => f.id === curr.flowId),
									tenant: this.tenants.find((t) => t.id === curr.tenantId),
									account: this.accounts.find((a) => a.id === curr.accountId),
									shared_ref: curr.shared_ref,
									flow_node: curr.flow_node,
									queue_id: curr.id
								})
							})
							return acc
						}, []) || []

					this.totalAccountCost = accountBilling
						.filter(
							(ab) => !this.selectedTenant || ab.account?.tenant?.id === parseInt(this.selectedTenant)
						)
						.reduce((acc, curr) => acc + (curr.currentBilled || 0), 0)

					this.totalTenantCost = tenantBilling
						.filter((tb) => !this.selectedTenant || tb.tenant?.id === parseInt(this.selectedTenant))
						.reduce((acc, curr) => acc + (curr.currentBilled || 0), 0)

					if (!this.stopPolling) setTimeout(this.reloadData, 2000)
				})
				.catch((error) => {
					console.log(error)
					if (!this.stopPolling) setTimeout(this.reloadData, 5000)
				})
		},

		updatePills() {
			this.pills = []

			if (this.selectedTenant) {
				this.addFilterPill(this.selectedTenantName, 'Tenant')
			}
			if (this.selectedAccount) {
				this.addFilterPill(this.selectedAccountName, 'Account')
			}
		},
		clearSelectedTenant() {
			this.selectedTenant = ''
			this.$global.setSelectedTenant({ target: { value: '' } })
		},

		removeFilterPill(pill) {
			const index = this.pills.findIndex((p) => p.name === pill.name && p.category === pill.category)
			if (index > -1) {
				this.pills.splice(index, 1)
			}

			switch (pill.category) {
				case 'Tenant':
					this.selectedTenant = ''
					break
			}

			this.reloadAll()
		}
	}
}
</script>
